import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { ACH_MINIMUM_DEPOSIT } from 'utils/constants.ts';
import { BankAccountProvider } from 'interfaces/commerce/bank-account.ts';

type PaymentMethod = 
  | { type: 'ACH'; bankAccountId: string; provider: BankAccountProvider }
  | { type: 'CARD'; inovioUserPaymentMethodId: string | 'NEW' }
  | { type: 'CRYPTO' };

interface DepositFundsModalState {
  show: boolean;
  autoOpenAchSection: boolean;
  selectedAmount: number;
  selectedPaymentMethod: PaymentMethod | null;
  processing: boolean;
  paymentMethodType: 'CARD' | 'ACH';
}

const initialState: DepositFundsModalState = {
  show: false,
  autoOpenAchSection: false,
  selectedAmount: 5, // Default amount
  selectedPaymentMethod: null,
  processing: false,
  paymentMethodType: 'ACH', // Default to ACH / Bank Transfer (expanded)
};

const depositFundsModalV2Slice = createSlice({
  name: 'depositFundsModalV2',
  initialState,
  reducers: {
    showDepositModalV2: (state, action: PayloadAction<boolean | undefined>) => {
      state.show = true;
      state.autoOpenAchSection = action.payload || false;
      if (action.payload) {
        state.paymentMethodType = 'ACH';
        // Clear selected amount if it's less than ACH minimum when switching to ACH
        if (state.selectedAmount < ACH_MINIMUM_DEPOSIT) {
          state.selectedAmount = 0;
        }
      }
    },
    hideDepositModalV2: (state) => {
      state.show = false;
      state.processing = false;
    },
    setSelectedAmount: (state, action: PayloadAction<number>) => {
      state.selectedAmount = action.payload;
    },
    setSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethod | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    setPaymentMethodType: (state, action: PayloadAction<'CARD' | 'ACH'>) => {
      state.paymentMethodType = action.payload;
      // Clear payment method when switching types
      state.selectedPaymentMethod = null;
      // Clear selected amount if switching to ACH and amount < ACH_MINIMUM_DEPOSIT
      if (action.payload === 'ACH' && state.selectedAmount < ACH_MINIMUM_DEPOSIT) {
        state.selectedAmount = 0;
      }
    },
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.processing = action.payload;
    },
    resetDepositModalState: (state) => {
      return { ...initialState };
    },
  },
});

export const {
  showDepositModalV2,
  hideDepositModalV2,
  setSelectedAmount,
  setSelectedPaymentMethod,
  setPaymentMethodType,
  setProcessing,
  resetDepositModalState,
} = depositFundsModalV2Slice.actions;

export const getDepositFundsModalState = (state: RootState) => state.depositFundsModalV2;

export default depositFundsModalV2Slice.reducer; 